import { format as formatDateFns } from "date-fns";

const ISO_DATE = /^\d{4}-\d{2}-\d{2}(T\d{2}:\d{2}(:\d{2}(\.\d+)?)?(Z|[+-]\d{2}:?\d{2})?)?$/;

export function getFieldValue(data, field) {
  if (data == null || field == null || field === "") return undefined;
  if (typeof field === "function") return field(data);
  if (field.indexOf(".") < 0) return data[field];

  return field
    .split(".")
    .reduce((current, part) => (current == null ? undefined : current[part]), data);
}

export function normalizeDataType(dataType) {
  switch (dataType) {
    case "numeric":
    case "number":
      return "number";
    case "bool":
    case "boolean":
      return "boolean";
    case "date":
      return "date";
    case "datetime":
      return "datetime";
    case "object":
      return "object";
    default:
      return "string";
  }
}

export const isDateType = dataType => dataType === "date" || dataType === "datetime";

export function isIsoDateString(value) {
  return typeof value === "string" && ISO_DATE.test(value);
}

export function toDate(value) {
  if (value == null || value === "") return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  if (typeof value === "object" && typeof value.toDate === "function") return value.toDate();

  const date = new Date(value);

  return Number.isNaN(date.getTime()) ? null : date;
}

export function startOfDay(date) {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

export function addDays(date, days) {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate() + days);
}

export function startOfMinute(date) {
  return new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
    date.getHours(),
    date.getMinutes()
  );
}

export function addMinutes(date, minutes) {
  return new Date(date.getTime() + minutes * 60000);
}

function groupDigits(integer, separator) {
  return integer.replace(/\B(?=(\d{3})+(?!\d))/g, separator);
}

export function formatNumberPattern(value, pattern, decimalSeparator = ".") {
  const [integerPattern, fractionPattern = ""] = pattern.split(".");
  const minFraction = (fractionPattern.match(/0/g) || []).length;
  const maxFraction = minFraction + (fractionPattern.match(/#/g) || []).length;
  const minInteger = (integerPattern.match(/0/g) || []).length;
  const grouping = integerPattern.includes(",");
  const percent = pattern.includes("%");
  const number = percent ? value * 100 : value;

  let [integer, fraction = ""] = Math.abs(number).toFixed(maxFraction).split(".");

  while (fraction.length > minFraction && fraction.endsWith("0")) fraction = fraction.slice(0, -1);

  integer = integer.padStart(minInteger, "0");

  if (grouping) integer = groupDigits(integer, decimalSeparator === "," ? "." : ",");

  const sign = number < 0 && Number(`${integer}.${fraction || 0}`) !== 0 ? "-" : "";

  return `${sign}${integer}${fraction ? `${decimalSeparator}${fraction}` : ""}${
    percent ? "%" : ""
  }`;
}

function formatNumber(value, format, locale) {
  const number = typeof value === "number" ? value : Number(value);
  if (Number.isNaN(number)) return String(value);

  const decimalSeparator = locale?.decimalSeparator || ".";

  if (typeof format === "string") {
    if (/[#0]/.test(format)) return formatNumberPattern(number, format, decimalSeparator);

    return String(number).replace(".", decimalSeparator);
  }

  if (format && typeof format === "object") {
    const precision = format.precision ?? 0;

    switch (format.type) {
      case "fixedPoint":
        return formatNumberPattern(
          number,
          `#,##0${precision ? `.${"0".repeat(precision)}` : ""}`,
          decimalSeparator
        );
      case "decimal":
        return formatNumberPattern(
          number,
          `0${precision ? `.${"0".repeat(precision)}` : ""}`,
          decimalSeparator
        );
      case "percent":
        return formatNumberPattern(
          number,
          `0${precision ? `.${"0".repeat(precision)}` : ""}%`,
          decimalSeparator
        );
      default:
        break;
    }
  }

  return String(number).replace(".", decimalSeparator);
}

function defaultDateText(date, dataType) {
  if (dataType === "datetime")
    return date.toLocaleString(undefined, { dateStyle: "short", timeStyle: "short" });

  return date.toLocaleDateString();
}

function formatDate(value, format, dataType) {
  const date = toDate(value);
  if (!date) return value == null ? "" : String(value);

  if (typeof format === "string" && format) {
    try {
      return formatDateFns(date, format);
    } catch {
      return defaultDateText(date, dataType);
    }
  }

  return defaultDateText(date, dataType);
}

export function formatValue(value, column, locale) {
  if (value == null) return "";

  const { format, dataType } = column;

  if (typeof format === "function") return format(value) ?? "";
  if (format && typeof format.formatter === "function") return format.formatter(value) ?? "";

  if (isDateType(dataType) || value instanceof Date) return formatDate(value, format, dataType);

  if (dataType === "number" && (typeof value === "number" || typeof value === "string"))
    return formatNumber(value, format, locale);

  if (dataType === "boolean" || typeof value === "boolean") {
    if (value === true) return column.trueText ?? locale?.trueText ?? "true";
    if (value === false) return column.falseText ?? locale?.falseText ?? "false";
  }

  if (typeof value === "number") return formatNumber(value, format, locale);

  if (Array.isArray(value)) return value.join(", ");

  if (typeof value === "object") return "";

  return String(value);
}

export function calculateCellValue(column, data) {
  if (data == null) return undefined;
  if (column.calculateCellValue) return column.calculateCellValue.call(column, data);

  return getFieldValue(data, column.dataField);
}

export function calculateDisplayValue(column, data, value) {
  const { calculateDisplayValue } = column;

  if (!calculateDisplayValue || data == null) return value;
  if (typeof calculateDisplayValue === "function") return calculateDisplayValue.call(column, data);

  return getFieldValue(data, calculateDisplayValue);
}

export function customizeValueText(column, value, valueText, target, extra) {
  if (!column.customizeText) return valueText;

  const text = column.customizeText.call(column, { value, valueText, target, ...extra });

  return text == null ? "" : String(text);
}

export function getValueText(column, value, locale, target = "row") {
  return customizeValueText(column, value, formatValue(value, column, locale), target);
}

export function getCellText(column, data, value, locale, target = "row") {
  const displayValue = calculateDisplayValue(column, data, value);

  return customizeValueText(
    column,
    displayValue,
    formatValue(displayValue, column, locale),
    target
  );
}

export function getGroupKeyText(column, key, locale) {
  if (key == null || key === "") return locale?.blankText ?? "";

  return getValueText(column, key, locale, "group");
}
